import axios, { AxiosError, AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import { secureStorage } from '../utils';

const baseUrl = process.env.REACT_APP_BASE_URL || '/api';

export const getAccessToken = (): string | null => {
  return secureStorage.getAccessToken();
};

export const axiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Single-flight token refresh to prevent concurrent refresh storms
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

function isAuthEndpoint(url?: string): boolean {
  return Boolean(
    url?.includes('/auth/login') ||
    url?.includes('/auth/refresh') ||
    url?.includes('/auth/2fa/') ||
    url?.includes('/auth/forgot-password') ||
    url?.includes('/auth/reset-password'),
  );
}

const LOGIN_PATH = '/login';

/** Guards against a burst of parallel 401s each firing their own navigation. */
let isRedirectingToLogin = false;

/**
 * Ends the session and sends the user back to the login screen. Called for any
 * 401 the refresh flow cannot recover from (no refresh token, refresh rejected,
 * or a retried request that came back unauthorised again).
 */
export function redirectToLogin(): void {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;

  secureStorage.clearAll();

  if (typeof window === 'undefined') return;

  if (window.location.pathname === LOGIN_PATH) {
    // Already on the login screen — nothing to navigate to, so let a later
    // 401 (e.g. after a fresh failed attempt) redirect again if it needs to.
    isRedirectingToLogin = false;
    return;
  }

  window.location.href = LOGIN_PATH;
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (config.headers.Authorization !== false && token) {
      (config.headers as AxiosHeaders).set('Authorization', `Bearer ${token}`);
    } else {
      delete config.headers.Authorization;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const data = error.response?.data as { errorCode?: string; error?: string; message?: string } | undefined;

    // Skip refresh for auth and 2FA endpoints — let callers handle 401 directly
    if (!originalRequest || isAuthEndpoint(originalRequest.url)) {
      throw error;
    }

    const refreshToken = secureStorage.getRefreshToken();

    // Handle 401 — attempt token refresh
    if (status === 401 && !originalRequest._retry && refreshToken) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${baseUrl}/auth/refresh`, { refreshToken });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        secureStorage.setTokens(accessToken, newRefreshToken);

        processQueue(null, accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return await axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 401 with nothing left to try: no refresh token, or the retried request
    // came back unauthorised again. End the session and go to login.
    if (status === 401) {
      redirectToLogin();
      error.message = data?.message ?? 'Your session has expired. Please log in again.';
      throw error;
    }

    if (status === 429) {
      error.message = 'Too many requests. Please wait a moment before trying again.';
    } else if (status === 403 && data?.errorCode === 'IP_BLOCKED') {
      error.message =
        data?.message ?? 'Access has been blocked from this network. Please try again later or contact support.';
    } else if (status === 403 && data?.error === 'ACCOUNT_INACTIVE') {
      const inactiveMessage = data?.message ?? 'Your account has been deactivated.';
      error.message = inactiveMessage;
      window.dispatchEvent(
        new CustomEvent('account-inactive', {
          detail: { message: inactiveMessage },
        }),
      );
    }

    throw error;
  },
);

export default axiosInstance;
